<template>
  <controller :lockScroll="true" :goToTopButton="false">
    <section class="matriculas-v2-embed-page">
      <div v-if="loading || error" :class="['matriculas-v2-state', { error }]">
        <span v-if="loading" class="loader"></span>
        <p>{{ message }}</p>
        <button v-if="error" type="button" @click="startHandoff">
          Tentar novamente
        </button>
      </div>
      <iframe
        v-else
        ref="matriculasV2Frame"
        :src="redirectUrl"
        title="Matrículas V2"
        allow="clipboard-read; clipboard-write"
        @load="onIframeLoad"
      ></iframe>
    </section>
  </controller>
</template>

<script>
import CrmRematriculaService from '@/services/crm-rematricula.service'
import MatriculasV2Service from '@/services/matriculas-v2.service'

export default {
  name: 'MatriculasV2Embed',
  data () {
    return {
      error: null,
      loading: true,
      message: 'Abrindo Matrículas V2...',
      redirectUrl: null,
      redirectOverride: null,
      heartbeatTimer: null,
      reauthenticating: false,
      handoffRequestId: 0
    }
  },
  watch: {
    '$route.fullPath' () {
      this.redirectOverride = null
      this.startHandoff()
    }
  },
  mounted () {
    window.addEventListener('message', this.onEmbedMessage)
    window.addEventListener('matriculas-v2:apollo-logout', this.logoutIframe)
    this.startHandoff()
  },
  beforeUnmount () {
    this.handoffRequestId++
    this.stopHeartbeat()
    window.removeEventListener('message', this.onEmbedMessage)
    window.removeEventListener('matriculas-v2:apollo-logout', this.logoutIframe)
  },
  methods: {
    async startHandoff (redirectOverride = null) {
      const requestId = ++this.handoffRequestId
      const sanitizedRedirect = this.redirectFromHref(redirectOverride)
      if (sanitizedRedirect) {
        this.redirectOverride = sanitizedRedirect
      }

      this.stopHeartbeat()
      this.error = null
      this.loading = true
      this.message = 'Abrindo Matrículas V2...'
      this.redirectUrl = null

      try {
        if (!this.redirectOverride && this.$route.query.crm_negotiation) {
          const id = String(this.$route.query.crm_negotiation)
          if (!/^[1-9]\d*$/.test(id)) throw new Error('Negociação inválida.')
          const result = await CrmRematriculaService.renewalLink(id)
          if (requestId !== this.handoffRequestId) return
          this.redirectOverride = result.path
        }
        const redirectUrl = await MatriculasV2Service.redirectUrl({
          redirectTo: this.redirectTo(),
          embedded: true
        })
        if (requestId !== this.handoffRequestId) return
        this.redirectUrl = redirectUrl
        this.loading = false
      } catch (error) {
        if (requestId !== this.handoffRequestId) return
        this.error = error
        this.loading = false
        this.message = error.response?.data?.message || error.message || 'Não foi possível abrir o Matrículas V2.'
        window.toaster && window.toaster.emit('open', {
          style: 'warning',
          message: this.message,
          floater: true
        })
      }
    },
    redirectTo () {
      if (this.redirectOverride) {
        return this.redirectOverride
      }

      if (this.$route.query.redirect) {
        return this.$route.query.redirect
      }

      const routeRedirect = this.redirectFromRouteMeta()
      if (routeRedirect) {
        const query = this.queryString()
        const separator = routeRedirect.includes('?') ? '&' : '?'
        return query ? `${routeRedirect}${separator}${query}` : routeRedirect
      }

      const path = this.$route.params.action === 'nova'
        ? '/matriculas/nova'
        : '/matriculas/pesquisa'
      const query = this.queryString()

      return query ? `${path}?${query}` : path
    },
    redirectFromRouteMeta () {
      const routeRedirect = this.$route.meta?.matriculasV2RedirectTo

      if (typeof routeRedirect === 'function') {
        return routeRedirect(this.$route)
      }

      return typeof routeRedirect === 'string' ? routeRedirect : null
    },
    queryString () {
      const params = new URLSearchParams()

      Object.keys(this.$route.query || {}).forEach(key => {
        if (key === 'embed') return

        const value = this.$route.query[key]
        const values = Array.isArray(value) ? value : [value]
        values.forEach(item => {
          if (item !== undefined && item !== null && item !== '') {
            params.append(key, item)
          }
        })
      })

      return params.toString()
    },
    onIframeLoad () {
      this.startHeartbeat()
      this.sendIframeMessage('matriculas-v2:keepalive')
    },
    startHeartbeat () {
      this.stopHeartbeat()
      this.heartbeatTimer = window.setInterval(() => {
        this.sendIframeMessage('matriculas-v2:keepalive')
      }, 5 * 60 * 1000)
    },
    stopHeartbeat () {
      if (this.heartbeatTimer) {
        window.clearInterval(this.heartbeatTimer)
        this.heartbeatTimer = null
      }
    },
    sendIframeMessage (type) {
      this.$refs.matriculasV2Frame?.contentWindow?.postMessage({ type }, '*')
    },
    logoutIframe () {
      this.sendIframeMessage('matriculas-v2:logout')
    },
    onEmbedMessage (event) {
      if (event.source !== this.$refs.matriculasV2Frame?.contentWindow) {
        return
      }

      const payload = event.data || {}
      if (payload.source !== 'matriculas-v2') {
        return
      }

      if (payload.type === 'matriculas-v2:ready' && payload.href) {
        const redirect = this.redirectFromHref(payload.href)
        if (redirect) this.redirectOverride = redirect
      }

      if (payload.type === 'matriculas-v2:session-expired') {
        this.reauthenticate(payload.href)
      }
    },
    reauthenticate (href) {
      if (this.reauthenticating) {
        return
      }

      this.reauthenticating = true
      this.startHandoff(href)
      window.setTimeout(() => {
        this.reauthenticating = false
      }, 1500)
    },
    redirectFromHref (href) {
      if (!href || typeof href !== 'string') {
        return null
      }

      try {
        const url = new URL(href, window.location.origin)
        const redirect = `${url.pathname}${url.search}`
        return redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : null
      } catch {
        return null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.matriculas-v2-embed-page {
  background-color: $color-bg;
  display: flex;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  position: relative;
  width: 100%;

  iframe {
    border: 0;
    display: block;
    flex: 1;
    height: 100%;
    min-height: 0;
    width: 100%;
  }
}

.matriculas-v2-state {
  align-items: center;
  color: $color-text;
  display: flex;
  flex: 1;
  flex-direction: column;
  font-weight: bold;
  gap: $hmg_small;
  justify-content: center;
  padding: $mg;
  text-align: center;

  p {
    margin: 0;
  }

  button {
    background-color: $color-primary;
    border: 0;
    border-radius: 24px;
    color: $color-secondary;
    cursor: pointer;
    font-weight: bold;
    min-height: 36px;
    padding: 0 $hmg_mid;
  }

  &.error {
    color: $color-primary;
  }
}
</style>
